import {
  IFrontVariation,
  IFrontVariationProduct,
  IProduct,
  IProductResponse,
  ISubCategoryCharacteristic
} from 'models/product'
import { capitalizeFirstLetter } from './characteristicsValidation'

// Бэкенд отдаёт вариации полными документами, но в схеме API у товара нет полей отключения
export const toVariationProduct = (
  product: IProduct | IProductResponse,
  name: string = product.name
): IFrontVariationProduct => {
  const { disabled_by, disabled_reasons } = product as IProductResponse
  return {
    id: product._id,
    name,
    price: product.price,
    status: product.status,
    disabled_by,
    disabled_reasons
  }
}

/**
 * Проверяет, совместимо ли текущее значение с новой характеристикой
 * @param currentValue - текущее значение характеристики
 * @param newCharacteristic - новая характеристика подкатегории
 * @returns true если значение совместимо, false если нужно сбросить
 */
const isValueCompatibleWithCharacteristic = (
  currentValue: string,
  newCharacteristic: ISubCategoryCharacteristic | undefined
): boolean => {
  // Если характеристика не найдена в подкатегории - оставляем значение (свободный ввод)
  if (!newCharacteristic) {
    return true
  }

  // Если характеристика позволяет любые значения - оставляем текущее
  if (newCharacteristic.isAnyValue) {
    return true
  }

  // Если есть фиксированный набор значений - проверяем совместимость
  if (newCharacteristic.hints && newCharacteristic.hints.length > 0) {
    return newCharacteristic.hints.includes(currentValue)
  }

  // По умолчанию оставляем значение
  return true
}

/**
 * Обновляет название характеристики во всех вариациях
 * @param variations - массив вариаций
 * @param position - позиция характеристики ('first' | 'second')
 * @param newName - новое название характеристики
 * @param subCategoryCharacteristics - характеристики подкатегории для проверки совместимости
 * @returns обновленный массив вариаций
 */
export const updateVariationCharacteristicName = (
  variations: IFrontVariation[],
  position: 'first' | 'second',
  newName: string,
  subCategoryCharacteristics: ISubCategoryCharacteristic[] = []
): IFrontVariation[] => {
  const capitalizedName = capitalizeFirstLetter(newName)
  
  // Находим характеристику подкатегории с таким же названием
  const matchingCharacteristic = subCategoryCharacteristics.find(
    (char) => char.name.toLowerCase() === capitalizedName.toLowerCase()
  )

  return variations.map((variation) => {
    const updatedVariation = { ...variation }

    if (position === 'first') {
      updatedVariation.firstCharacteristicName = capitalizedName
      
      // Проверяем совместимость текущего значения с новой характеристикой
      const currentValue = variation.firstCharacteristicValue || ''
      if (!isValueCompatibleWithCharacteristic(currentValue, matchingCharacteristic)) {
        updatedVariation.firstCharacteristicValue = ''
      }
    } else {
      updatedVariation.secondCharacteristicName = capitalizedName
      
      // Проверяем совместимость текущего значения с новой характеристикой
      const currentValue = variation.secondCharacteristicValue || ''
      if (!isValueCompatibleWithCharacteristic(currentValue, matchingCharacteristic)) {
        updatedVariation.secondCharacteristicValue = ''
      }
    }

    return updatedVariation
  })
}

/**
 * Создает новые вариации для второй характеристики
 * @param variations - существующие вариации
 * @param characteristicName - название новой характеристики
 * @param characteristicValues - значения новой характеристики
 * @returns массив новых вариаций
 */
export const createSecondCharacteristicVariations = (
  variations: IFrontVariation[],
  characteristicName: string,
  characteristicValues: string[]
): IFrontVariation[] => {
  const capitalizedName = capitalizeFirstLetter(characteristicName)
  const newVariations: IFrontVariation[] = []

  const clearedCreateCharacteristicValues = characteristicValues.filter((item) =>
    item.trim()
  )
  const clearedOldVariations = variations.filter((item) =>
    item.firstCharacteristicValue?.trim()
  )

  for (const newCharacteristicValue of clearedCreateCharacteristicValues) {
    for (const oldVariation of clearedOldVariations) {
      newVariations.push({
        ...oldVariation,
        product: undefined,
        secondCharacteristicName: capitalizedName,
        secondCharacteristicValue: newCharacteristicValue
      })
    }
  }

  // Добавляем пустую вариацию для ввода новых значений
  newVariations.push({
    firstCharacteristicName: variations[0].firstCharacteristicName,
    firstCharacteristicValue: '',
    secondCharacteristicName: capitalizedName,
    secondCharacteristicValue: ''
  })

  return newVariations
}

/**
 * Создает новые вариации для первой характеристики
 * @param characteristicName - название характеристики
 * @param characteristicValues - значения характеристики
 * @returns массив новых вариаций
 */
export const createFirstCharacteristicVariations = (
  characteristicName: string,
  characteristicValues: string[]
): IFrontVariation[] => {
  const capitalizedName = capitalizeFirstLetter(characteristicName)
  const variations: IFrontVariation[] = []

  // Добавляем вариации для каждого значения
  characteristicValues
    .filter((item) => item.trim())
    .forEach((item) => {
      variations.push({
        firstCharacteristicName: capitalizedName,
        firstCharacteristicValue: item
      })
    })

  // Добавляем пустую вариацию для ввода новых значений
  variations.push({
    firstCharacteristicName: capitalizedName,
    firstCharacteristicValue: ''
  })

  return variations
}



/**
 * Проверяет, нужно ли добавить новую пустую вариацию
 * @param variations - массив вариаций
 * @returns true если нужно добавить новую вариацию
 */
export const shouldAddNewVariation = (variations: IFrontVariation[]): boolean => {
  if (!variations.length) return false

  // Хвостовая пустая строка нужна, как только последняя строка перестала быть пустой.
  // Проверка length === 1 ловила только посимвольный ввод: вставка значения из буфера
  // или выбор товара через модалку не добавляли строку, и заполненная строка оставалась последней
  const lastVariation = variations[variations.length - 1]
  return (
    !!lastVariation.firstCharacteristicValue?.trim() ||
    !!lastVariation.secondCharacteristicValue?.trim() ||
    !!lastVariation.product
  )
}

/**
 * Создает новую пустую вариацию на основе последней
 * @param lastVariation - последняя вариация
 * @returns новая пустая вариация
 */
export const createNewEmptyVariation = (
  lastVariation: IFrontVariation
): IFrontVariation => {
  if (lastVariation.secondCharacteristicName) {
    return {
      firstCharacteristicName: lastVariation.firstCharacteristicName,
      firstCharacteristicValue: '',
      secondCharacteristicName: lastVariation.secondCharacteristicName,
      secondCharacteristicValue: ''
    }
  } else {
    return {
      firstCharacteristicName: lastVariation.firstCharacteristicName,
      firstCharacteristicValue: ''
    }
  }
}

/**
 * Проверяет, нужно ли очистить массив вариаций
 * @param variations - массив вариаций
 * @returns true если нужно очистить массив
 */
export const shouldClearVariations = (variations: IFrontVariation[]): boolean => {
  return (
    variations.length === 1 &&
    !variations[0].firstCharacteristicValue &&
    !variations[0].secondCharacteristicValue
  )
}